import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL, CENTER_DATA } from './constants';
import AuthForms from './AuthForms';
import CheckoutScreen from './CheckoutScreen';

function PackagesPage({ onBackToBooking, guestInfo, authFormProps, setSelectedCenter }) {
  const [pkgStep, setPkgStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Location
  const [selectedLocation, setSelectedLocation] = useState(null);
  const centers = Object.values(CENTER_DATA);

  // Packages
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Category filter
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Payment
  const [invoiceId, setInvoiceId] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentIframeUrl, setPaymentIframeUrl] = useState(null);

  // Saved cards
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loadingCards, setLoadingCards] = useState(false);
  const [payError, setPayError] = useState('');
  const [paymentMode, setPaymentMode] = useState(null); // 'direct' or 'add-card'

  const iframeRef = useRef(null);

  // Listen for iframe navigation (payment completion or card added)
  const handleIframeLoad = useCallback(() => {
    try {
      const iframeSrc = iframeRef.current?.contentWindow?.location?.href;
      if (iframeSrc && iframeSrc.includes('pkg_paid=1')) {
        if (paymentMode === 'add-card') {
          // Card was added — now fetch saved cards and auto-pay
          handleCardAddedThenPay();
        } else {
          setPaymentIframeUrl(null);
          handlePaymentSuccess();
        }
      }
    } catch (e) {
      // Cross-origin — still on payment provider page, keep waiting
    }
  }, [paymentMode]);

  // Auto-proceed after auth completes
  useEffect(() => {
    if (pkgStep === 3 && guestInfo) {
      handleCreateInvoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestInfo, pkgStep]);

  // Fetch saved cards when reaching checkout
  useEffect(() => {
    if (pkgStep === 'checkout' && guestInfo?.guestId) {
      setLoadingCards(true);
      setSavedCards([]);
      setSelectedCard(null);
      setPayError('');
      setPaymentIframeUrl(null);
      const cid = selectedLocation?.id || '';
      const token = guestInfo?.webApiToken || '';
      fetch(`${API_URL}/api/kiosk/guest-cards/${guestInfo.guestId}?centerId=${cid}&webApiToken=${encodeURIComponent(token)}`)
        .then(r => r.json())
        .then(data => {
          if (data.success && data.cards?.length > 0) {
            setSavedCards(data.cards);
          }
        })
        .catch(() => {})
        .finally(() => setLoadingCards(false));
    }
  }, [pkgStep, guestInfo?.guestId, selectedLocation?.id]);

  async function fetchPackages(centerId) {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/packages/${centerId}?refresh=1`);
      const data = await res.json();
      if (data.success) {
        const pkgs = data.packages || [];
        setPackages(pkgs);
        if (pkgs.length > 0) {
          const cats = new Map();
          pkgs.forEach(pkg => {
            const cat = getPackageCategory(pkg);
            cats.set(cat, (cats.get(cat) || 0) + 1);
          });
          const sorted = Array.from(cats.entries()).sort((a, b) => a[1] - b[1]);
          handleCategorySelect(sorted[0][0]);
        }
      } else {
        setError(data.error || 'Failed to load packages.');
      }
    } catch (err) {
      console.error('Failed to fetch packages:', err);
      setError('Failed to load packages. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function getPackageCategory(pkg) {
    const serviceName = pkg.services?.[0]?.name || pkg.name;
    if (serviceName.toLowerCase().startsWith('laser')) return 'Laser';
    if (serviceName.toLowerCase().includes('spray tan')) return 'Spray Tan';
    if (serviceName.toLowerCase().includes('lash lift') || serviceName.toLowerCase().includes('inlei')) return 'Lash Lift';
    if (serviceName.toLowerCase().includes('brow lam')) return 'Brow Lamination';
    return serviceName;
  }

  function getCategories() {
    const cats = new Map();
    packages.forEach(pkg => {
      const cat = getPackageCategory(pkg);
      cats.set(cat, (cats.get(cat) || 0) + 1);
    });
    return Array.from(cats.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([name, count]) => ({ name, count }));
  }

  function getPackageSubCategory(pkg) {
    const name = pkg.name || '';
    if (name.includes('8-Pack') || name.includes('8 - Pack') || name.includes('8 Pack')) return '8-Pack';
    if (name.includes('10-Pack') || name.includes('10 Pack')) return '10-Pack';
    if (name.includes('6-Pack') || name.includes('6 - Pack') || name.includes('6 Pack')) return '6-Pack';
    if (name.includes('3-Pack') || name.includes('3 - Pack') || name.includes('3 Pack')) return '3-Pack';
    return null;
  }

  function getSubCategories() {
    if (!selectedCategory || selectedCategory !== 'Laser') return [];
    const catPackages = packages.filter(pkg => getPackageCategory(pkg) === selectedCategory);
    const subs = new Map();
    catPackages.forEach(pkg => {
      const sub = getPackageSubCategory(pkg);
      if (sub) subs.set(sub, true);
    });
    const keys = Array.from(subs.keys());
    if (keys.length <= 1) return [];
    return keys.sort((a, b) => parseInt(a) - parseInt(b));
  }

  function getFilteredPackages() {
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      return packages.filter(pkg =>
        pkg.name.toLowerCase().includes(q) ||
        (pkg.description || '').toLowerCase().includes(q) ||
        pkg.services?.some(s => s.name.toLowerCase().includes(q))
      );
    }
    if (!selectedCategory) return [];
    let filtered = packages.filter(pkg => getPackageCategory(pkg) === selectedCategory);
    if (selectedSubCategory !== 'all') {
      filtered = filtered.filter(pkg => getPackageSubCategory(pkg) === selectedSubCategory);
    }
    return filtered;
  }

  function handleCategorySelect(catName) {
    setSelectedCategory(catName);
    setSearchQuery('');
    if (catName === 'Laser') {
      const catPackages = packages.filter(pkg => getPackageCategory(pkg) === catName);
      const subs = new Map();
      catPackages.forEach(pkg => {
        const sub = getPackageSubCategory(pkg);
        if (sub) subs.set(sub, true);
      });
      const keys = Array.from(subs.keys()).sort((a, b) => parseInt(a) - parseInt(b));
      setSelectedSubCategory(keys.length > 1 ? keys[0] : 'all');
    } else {
      setSelectedSubCategory('all');
    }
  }

  function handleLocationSelect(center) {
    setSelectedLocation(center);
    setSelectedCenter(center);
    setSelectedCategory(null);
    setSelectedSubCategory('all');
    setSearchQuery('');
    setError('');
    fetchPackages(center.id);
    setPkgStep(2);
  }

  function handlePackageSelect(pkg) {
    setSelectedPackage(pkg);
    setError('');
    if (!guestInfo) {
      setPkgStep(3);
    } else {
      handleCreateInvoice(pkg);
    }
  }

  async function handleCreateInvoice(pkg) {
    const thePkg = pkg || selectedPackage;
    if (!thePkg || !guestInfo || !selectedLocation) {
      setError('Something went wrong. Please select a package and try again.');
      setPkgStep(2);
      return;
    }

    setPkgStep('creating');
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/packages/create-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: guestInfo.guestId,
          packageId: thePkg.id,
          centerId: selectedLocation.id,
          webApiToken: guestInfo.webApiToken
        })
      });
      const data = await res.json();

      if (data.success) {
        setInvoiceId(data.invoiceId);
        setPkgStep('checkout');
      } else {
        console.error('Package create-invoice failed:', data);
        const debugStr = data._debug ? `\n\nDebug: ${JSON.stringify(data._debug)}` : '';
        setError((data.error || 'Could not create invoice. Please try again.') + debugStr);
        setPkgStep(2);
      }
    } catch (err) {
      console.error('Package create-invoice error:', err);
      setError('Something went wrong. Please try again.');
      setPkgStep(2);
    }
  }

  // Pay with a saved card on file
  async function handlePayWithSavedCard() {
    if (!selectedCard) return;
    setPaymentLoading(true);
    setPayError('');
    try {
      const res = await fetch(`${API_URL}/api/kiosk/pay-saved-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceId,
          accountId: selectedCard.accountId,
          centerId: selectedLocation.id,
          tipAmount: 0
        })
      });
      const data = await res.json();
      if (data.success) {
        handlePaymentSuccess();
      } else if (data.requires3DS && data.hostedPaymentUrl) {
        // 3DS required — show in iframe
        setPaymentIframeUrl(data.hostedPaymentUrl);
      } else {
        setPayError(data.error || 'Payment failed. Please try another method.');
      }
    } catch {
      setPayError('Payment failed. Please try again.');
    }
    setPaymentLoading(false);
  }

  // Pay with a new credit card — show inline iframe
  async function handlePayWithNewCard() {
    setPaymentLoading(true);
    setPayError('');
    setSelectedCard(null);

    try {
      const res = await fetch(`${API_URL}/api/invoice/payment-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceId,
          redirectParam: 'pkg_paid=1',
          webApiToken: guestInfo?.webApiToken,
          centerId: selectedLocation?.id,
          guestId: guestInfo?.guestId
        })
      });
      const data = await res.json();

      if (data.success && data.hostedPaymentUrl) {
        setPaymentMode(data.mode === 'add-card' ? 'add-card' : 'direct');
        setPaymentIframeUrl(data.hostedPaymentUrl);
      } else {
        console.error('Payment URL error:', data);
        setPayError(data.error || 'Could not start payment. Please try again.');
      }
    } catch (err) {
      console.error('Payment URL fetch error:', err);
      setPayError('Something went wrong. Please try again.');
    }
    setPaymentLoading(false);
  }

  // After card is added via iframe, fetch the new card and auto-pay
  async function handleCardAddedThenPay() {
    setPaymentIframeUrl(null);
    setPaymentLoading(true);
    setPayError('');
    try {
      // Fetch saved cards to get the newly added card
      const cid = selectedLocation?.id || '';
      const token = guestInfo?.webApiToken || '';
      const cardsRes = await fetch(`${API_URL}/api/kiosk/guest-cards/${guestInfo.guestId}?centerId=${cid}&webApiToken=${encodeURIComponent(token)}`);
      const cardsData = await cardsRes.json();
      if (cardsData.success && cardsData.cards?.length > 0) {
        setSavedCards(cardsData.cards);
        const card = cardsData.cards[0]; // Use the first (most recent) card
        // Auto-pay with the new card
        const payRes = await fetch(`${API_URL}/api/kiosk/pay-saved-card`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoiceId, accountId: card.accountId, centerId: selectedLocation?.id, tipAmount: 0 })
        });
        const payData = await payRes.json();
        if (payData.success) {
          handlePaymentSuccess();
          return;
        }
        setPayError(payData.error || 'Payment failed after adding card. Please try again.');
      } else {
        setPayError('Card was saved but could not be retrieved. Please try selecting it from the list.');
      }
    } catch (err) {
      console.error('Auto-pay after card add failed:', err);
      setPayError('Payment failed. Please select your card and try again.');
    }
    setPaymentLoading(false);
  }

  async function handlePaymentSuccess() {
    setPaymentLoading(false);
    setPaymentIframeUrl(null);
    try {
      await fetch(`${API_URL}/api/packages/confirm-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId })
      });
    } catch (e) {
      console.error('Package confirmation email error:', e);
    }
    setPkgStep(5);
  }

  function handleBrowseMore() {
    setSelectedPackage(null);
    setInvoiceId(null);
    setError('');
    setPayError('');
    setSavedCards([]);
    setSelectedCard(null);
    setPaymentIframeUrl(null);
    setPkgStep(2);
  }

  function handleStartOver() {
    setPkgStep(1);
    setSelectedLocation(null);
    setSelectedPackage(null);
    setPackages([]);
    setInvoiceId(null);
    setError('');
    setPayError('');
    setSearchQuery('');
    setSavedCards([]);
    setSelectedCard(null);
    setPaymentIframeUrl(null);
  }

  const cardIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('visa')) return 'Visa';
    if (t.includes('master')) return 'Mastercard';
    if (t.includes('amex')) return 'Amex';
    return 'Card';
  };

  return (
    <div className="step-content">
      {/* Step 1: Location Selection */}
      {pkgStep === 1 && (
        <>
          <h1 className="step-title">PURCHASE A PACKAGE</h1>
          <p className="step-subtitle">Select a location to view available packages</p>

          <div className="location-cards-grid">
            {centers.map(c => (
              <div
                key={c.id}
                className={`location-card ${selectedLocation?.id === c.id ? 'selected' : ''}`}
                onClick={() => handleLocationSelect(c)}
              >
                <div className="location-icon">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                </div>
                <h3>{c.name}</h3>
                <p className="location-address">{c.address}</p>
                <div className="location-cta">Select Location</div>
              </div>
            ))}
          </div>

          <button
            className="auth-button"
            style={{ marginTop: '24px', background: 'transparent', color: '#999', border: '1.5px solid #ddd', maxWidth: '400px', width: '100%' }}
            onClick={onBackToBooking}
          >
            BACK TO BOOKING
          </button>
        </>
      )}

      {/* Step 2: Package Browsing */}
      {pkgStep === 2 && (
        <>
          <div className="pkg-location-switcher">
            {centers.map(c => (
              <button
                key={c.id}
                className={`pkg-location-pill ${selectedLocation?.id === c.id ? 'active' : ''}`}
                onClick={() => {
                  if (c.id !== selectedLocation?.id) {
                    handleLocationSelect(c);
                  }
                }}
              >
                {c.name}
              </button>
            ))}
          </div>

          <h1 className="step-title">PACKAGES</h1>
          <p className="step-subtitle">Packages can be used at any location that offers the service.</p>

          {/* Search Bar */}
          <div className="pkg-search-wrapper">
            <svg className="pkg-search-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#aaa" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              className="pkg-search-input"
              placeholder="Search for a Package"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="pkg-search-clear" onClick={() => setSearchQuery('')}>&times;</button>
            )}
          </div>

          {error && <div className="error-banner">{error}</div>}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div className="spinner" style={{ margin: '0 auto 24px' }}></div>
              <p style={{ color: '#888', fontSize: '14px' }}>Loading packages...</p>
            </div>
          ) : packages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <p style={{ color: '#888', fontSize: '16px' }}>No packages available at this location.</p>
            </div>
          ) : (
            <>
              {!searchQuery.trim() && (
                <>
                  <div className="pkg-category-tabs">
                    {getCategories().map(cat => (
                      <button
                        key={cat.name}
                        className={`pkg-category-tab ${selectedCategory === cat.name ? 'active' : ''}`}
                        onClick={() => handleCategorySelect(cat.name)}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>

                  {getSubCategories().length > 0 && (
                    <div className="pkg-subcategory-tabs">
                      {getSubCategories().map(sub => (
                        <button
                          key={sub}
                          className={`pkg-subcategory-tab ${selectedSubCategory === sub ? 'active' : ''}`}
                          onClick={() => setSelectedSubCategory(sub)}
                        >
                          {sub}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="pkg-grid">
                {getFilteredPackages().map(pkg => (
                  <div key={pkg.id} className="pkg-card">
                    <div className="pkg-card-name">{pkg.name}</div>
                    {(pkg.htmlDescription || pkg.description) && (
                      pkg.htmlDescription
                        ? <div className="pkg-card-description" dangerouslySetInnerHTML={{ __html: pkg.htmlDescription }} />
                        : <div className="pkg-card-description">{pkg.description}</div>
                    )}
                    {pkg.services && pkg.services.length > 0 && (
                      <div className="pkg-card-services">
                        {pkg.services.map((s, i) => (
                          <span key={i} className="pkg-service-tag">
                            {s.quantity > 1 ? `${s.quantity}x ` : ''}{s.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {pkg.validity && (
                      <div className="pkg-card-validity">Valid for {pkg.validity} days</div>
                    )}
                    <div className="pkg-card-footer">
                      <div className="pkg-card-price">${pkg.price.toFixed(2)}</div>
                      <button className="pkg-card-cta" onClick={() => handlePackageSelect(pkg)}>
                        SELECT
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {searchQuery.trim() && getFilteredPackages().length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <p style={{ color: '#888', fontSize: '14px' }}>No packages match "{searchQuery}"</p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Step 3: Auth Check */}
      {pkgStep === 3 && !guestInfo && (
        <>
          <h1 className="step-title">SIGN IN TO CONTINUE</h1>
          <p className="step-subtitle">You need to be signed in to purchase the {selectedPackage?.name} package</p>

          {error && <div className="error-banner">{error}</div>}

          <AuthForms variant="standalone" {...authFormProps} />
        </>
      )}

      {/* Creating invoice loading */}
      {pkgStep === 'creating' && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 24px' }}></div>
          <p style={{ color: '#888', fontSize: '14px' }}>Preparing your order...</p>
        </div>
      )}

      {/* Checkout + Payment */}
      {pkgStep === 'checkout' && (
        <>
          {/* Payment iframe (full width, replaces checkout card when active) */}
          {paymentIframeUrl ? (
            <div className="pkg-iframe-section">
              <h1 className="step-title">{paymentMode === 'add-card' ? 'ADD PAYMENT METHOD' : 'COMPLETE PAYMENT'}</h1>
              <p className="step-subtitle">{paymentMode === 'add-card' ? 'Add your card to complete the purchase' : 'Enter your card details below'}</p>
              <div className="pkg-iframe-wrapper">
                <iframe
                  ref={iframeRef}
                  src={paymentIframeUrl}
                  title="Payment"
                  className="pkg-payment-iframe"
                  onLoad={handleIframeLoad}
                />
              </div>
              <button
                className="pkg-checkout-back-btn"
                onClick={() => { setPaymentIframeUrl(null); setPayError(''); }}
              >
                Cancel Payment
              </button>
            </div>
          ) : (
            <>
              <h1 className="step-title">CHECKOUT</h1>
              <p className="step-subtitle">Review your order</p>

              <div className="pkg-checkout-grid">
                {/* Left: Invoice */}
                <div className="pkg-checkout-left">
                  <CheckoutScreen
                    itemType="package"
                    itemName={selectedPackage?.name}
                    itemDetails={[
                      ...(selectedPackage?.services?.length > 0 ? [{
                        label: 'Includes',
                        value: selectedPackage.services.map(s => `${s.quantity > 1 ? s.quantity + 'x ' : ''}${s.name}`).join(', ')
                      }] : []),
                      { label: 'Location', value: selectedLocation?.name },
                      ...(selectedPackage?.validity ? [{ label: 'Valid For', value: `${selectedPackage.validity} days` }] : [])
                    ]}
                    invoiceId={invoiceId}
                    originalPrice={selectedPackage?.price}
                    centerId={selectedLocation?.id}
                    hideButtons
                  />

                  <button
                    className="pkg-add-another-btn"
                    onClick={handleBrowseMore}
                  >
                    + Add Another Package
                  </button>
                </div>

                {/* Right: Payment */}
                <div className="pkg-checkout-right">
                  <div className="pkg-payment-section">
                    <h2 className="pkg-payment-title">Make a Payment</h2>

                    {payError && <div className="error-banner" style={{ marginBottom: '16px' }}>{payError}</div>}
                    {loadingCards && <p style={{ color: '#888', fontSize: '14px', textAlign: 'center' }}>Loading payment methods...</p>}

                    {savedCards.length > 0 && savedCards.map(card => (
                      <div
                        key={card.accountId}
                        className={`pkg-pay-card-row ${selectedCard?.accountId === card.accountId ? 'active' : ''}`}
                        onClick={() => setSelectedCard(card)}
                      >
                        <span className="pkg-pay-card-info">
                          {cardIcon(card.cardType)} ending with {card.lastFour}
                        </span>
                        <div className={`pkg-pay-radio ${selectedCard?.accountId === card.accountId ? 'checked' : ''}`}>
                          {selectedCard?.accountId === card.accountId && <span className="pkg-pay-radio-dot" />}
                        </div>
                      </div>
                    ))}

                    {selectedCard && (
                      <button
                        className="auth-button pkg-pay-btn"
                        onClick={handlePayWithSavedCard}
                        disabled={paymentLoading}
                      >
                        {paymentLoading ? 'Processing...' : 'PAY NOW'}
                      </button>
                    )}

                    <div
                      className={`pkg-pay-card-row pkg-pay-new-card ${!selectedCard && savedCards.length === 0 ? 'active' : ''}`}
                      onClick={() => handlePayWithNewCard()}
                    >
                      <span className="pkg-pay-card-info">
                        {savedCards.length > 0 ? 'Use a New Payment Method' : 'Pay with Credit Card'}
                      </span>
                      <div className="pkg-pay-radio">
                        <span className="pkg-pay-radio-dot" style={{ background: '#F78DA7' }} />
                      </div>
                    </div>

                    {paymentLoading && (
                      <p style={{ color: '#888', fontSize: '13px', textAlign: 'center', marginTop: '12px' }}>Loading payment form...</p>
                    )}

                    <button
                      className="pkg-checkout-back-btn"
                      onClick={() => { setPkgStep(2); setInvoiceId(null); setError(''); setPayError(''); }}
                      disabled={paymentLoading}
                    >
                      Back to Packages
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Step 5: Success */}
      {pkgStep === 5 && (
        <div className="booking-success">
          <div className="success-icon">&#10003;</div>
          <h2>Package Purchased!</h2>
          <p className="success-subtitle">
            Your {selectedPackage?.name} package has been added to your account.
          </p>

          <div className="success-details">
            <div className="success-detail-row">
              <span className="success-label">Package</span>
              <span className="success-value">{selectedPackage?.name}</span>
            </div>
            <div className="success-detail-row">
              <span className="success-label">Price</span>
              <span className="success-value">${selectedPackage?.price?.toFixed(2)}</span>
            </div>
            <div className="success-detail-row">
              <span className="success-label">Location</span>
              <span className="success-value">{selectedLocation?.name}</span>
            </div>
            {selectedPackage?.validity && (
              <div className="success-detail-row">
                <span className="success-label">Valid For</span>
                <span className="success-value">{selectedPackage.validity} days</span>
              </div>
            )}
          </div>

          <button
            className="auth-button"
            style={{ marginTop: '30px', width: '100%', maxWidth: '400px' }}
            onClick={onBackToBooking}
          >
            BACK TO BOOKING
          </button>

          <button
            className="auth-button"
            style={{ marginTop: '12px', width: '100%', maxWidth: '400px', background: 'transparent', color: '#F78DA7', border: '1.5px solid #F78DA7' }}
            onClick={handleBrowseMore}
          >
            BROWSE MORE PACKAGES
          </button>
        </div>
      )}

      {/* Fallback: catch unexpected states */}
      {![1, 2, 3, 5, 'creating', 'checkout'].includes(pkgStep) && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ color: '#888', fontSize: '16px', marginBottom: '20px' }}>Something went wrong.</p>
          <button className="auth-button" style={{ maxWidth: '300px', width: '100%' }} onClick={handleStartOver}>
            START OVER
          </button>
        </div>
      )}
    </div>
  );
}

export default PackagesPage;
